<script setup lang="ts">
import { computed, ref } from 'vue'
import type { Component } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useCustomersStore } from '@/stores/customers'
import { isLocalDevAuthBypass } from '@/config/runtimeEnv'
import { useSidebarNav, type SidebarSectionId } from '@/composables/useSidebarNav'
import SidebarNavItem from './SidebarNavItem.vue'
import SidebarSection from './SidebarSection.vue'
import {
  HomeIcon,
  BeakerIcon,
  ArrowRightStartOnRectangleIcon,
  UsersIcon,
  XMarkIcon,
  SparklesIcon,
  RocketLaunchIcon,
  CurrencyDollarIcon,
  ChatBubbleLeftRightIcon,
  ChatBubbleBottomCenterTextIcon,
  ExclamationTriangleIcon,
  MegaphoneIcon,
  MagnifyingGlassIcon,
  MicrophoneIcon,
  BellIcon,
  EnvelopeOpenIcon,
  PlayCircleIcon,
  GiftIcon,
  FlagIcon,
  UserPlusIcon,
} from '@heroicons/vue/24/outline'

defineProps<{ open?: boolean }>()
const emit = defineEmits<{ (e: 'close'): void }>()

const route = useRoute()
const authStore = useAuthStore()
const customersStore = useCustomersStore()
const localDev = isLocalDevAuthBypass()
const { isExpanded, toggle, move, visibleOrder } = useSidebarNav()

interface SidebarItem {
  to: string
  icon: Component
  label: string
  isActive: (path: string) => boolean
}

interface SidebarSectionConfig {
  id: SidebarSectionId
  label: string
  staffOnly: boolean
  items: SidebarItem[]
}

const exact = (path: string) => (current: string) => current === path

const sections: SidebarSectionConfig[] = [
  {
    id: 'overview',
    label: 'Overview',
    staffOnly: false,
    items: [
      { to: '/', icon: HomeIcon, label: 'Overview', isActive: exact('/') },
      { to: '/activation', icon: FlagIcon, label: 'Activation', isActive: exact('/activation') },
      {
        to: '/customers',
        icon: UsersIcon,
        label: 'Customers',
        isActive: (path) => path === '/customers' || path === '/users',
      },
      { to: '/revenue', icon: CurrencyDollarIcon, label: 'Revenue', isActive: exact('/revenue') },
      { to: '/analytics', icon: BeakerIcon, label: 'Product Analytics', isActive: exact('/analytics') },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    staffOnly: true,
    items: [
      {
        to: '/user-feedback',
        icon: ChatBubbleBottomCenterTextIcon,
        label: 'User Feedback',
        isActive: (path) => path.startsWith('/user-feedback'),
      },
      {
        to: '/sessions',
        icon: PlayCircleIcon,
        label: 'Sessions',
        isActive: (path) => path.startsWith('/sessions'),
      },
      { to: '/onboarding', icon: RocketLaunchIcon, label: 'Onboarding', isActive: exact('/onboarding') },
      { to: '/setup-leads', icon: UserPlusIcon, label: 'Setup Leads', isActive: exact('/setup-leads') },
      { to: '/email-ops', icon: EnvelopeOpenIcon, label: 'Email Ops', isActive: exact('/email-ops') },
      { to: '/agent-conversations', icon: ChatBubbleLeftRightIcon, label: 'Agent Conversations', isActive: exact('/agent-conversations') },
      { to: '/agent-capability-requests', icon: ExclamationTriangleIcon, label: 'Capability Requests', isActive: exact('/agent-capability-requests') },
      {
        to: '/referrals',
        icon: GiftIcon,
        label: 'Referrals',
        isActive: (path) => path.startsWith('/referrals'),
      },
      {
        to: '/ambassador-applications',
        icon: SparklesIcon,
        label: 'Applications',
        isActive: (path) => path.startsWith('/ambassador-applications'),
      },
      { to: '/voice-skills', icon: MicrophoneIcon, label: 'Voice & Skills', isActive: exact('/voice-skills') },
      { to: '/ai-spend', icon: CurrencyDollarIcon, label: 'AI Spend', isActive: exact('/ai-spend') },
      { to: '/announcements', icon: MegaphoneIcon, label: 'Announcements', isActive: exact('/announcements') },
      { to: '/notifications', icon: BellIcon, label: 'Push Notifications', isActive: exact('/notifications') },
    ],
  },
]

const sectionsById = Object.fromEntries(sections.map((section) => [section.id, section])) as Record<
  SidebarSectionId,
  SidebarSectionConfig
>

const visibleSections = computed(() => {
  const staff = authStore.canAccessStaff
  const visible = sections.filter((section) => !section.staffOnly || staff)
  return visibleOrder(visible.map((section) => section.id))
    .map((id) => sectionsById[id])
    .filter(Boolean)
})

const reorderable = computed(() => visibleSections.value.length > 1)

const draggingId = ref<SidebarSectionId | null>(null)
const dropHint = ref<{ id: SidebarSectionId; place: 'before' | 'after' } | null>(null)

function onSectionDragStart(id: SidebarSectionId) {
  draggingId.value = id
}

function onSectionDragOver(id: SidebarSectionId, event: DragEvent) {
  if (!draggingId.value) return
  event.preventDefault()
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
  const el = event.currentTarget as HTMLElement | null
  if (!el) return
  const rect = el.getBoundingClientRect()
  const place = event.clientY < rect.top + rect.height / 2 ? 'before' : 'after'
  dropHint.value = { id, place }
}

function dropPlaceForEvent(id: SidebarSectionId, event: DragEvent): 'before' | 'after' {
  if (dropHint.value?.id === id) return dropHint.value.place
  const el = event.currentTarget as HTMLElement | null
  if (!el) return 'before'
  const rect = el.getBoundingClientRect()
  return event.clientY < rect.top + rect.height / 2 ? 'before' : 'after'
}

function onSectionDrop(id: SidebarSectionId, event: DragEvent) {
  event.preventDefault()
  if (draggingId.value) {
    move(draggingId.value, id, dropPlaceForEvent(id, event))
  }
  draggingId.value = null
  dropHint.value = null
}

function onSectionDragEnd() {
  draggingId.value = null
  dropHint.value = null
}

function sectionHasActive(section: SidebarSectionConfig): boolean {
  return section.items.some((item) => item.isActive(route.path))
}
</script>

<template>
  <!-- Mobile backdrop -->
  <Transition
    enter-active-class="transition-opacity ease-linear duration-200"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-opacity ease-linear duration-200"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="open"
      class="fixed inset-0 z-30 bg-black/40 md:hidden"
      @click="emit('close')"
    />
  </Transition>

  <!-- Sidebar: static on md+, sliding drawer on mobile -->
  <aside
    class="fixed inset-y-0 left-0 z-40 flex h-screen w-64 flex-col border-r border-gray-200 bg-white transition-transform duration-200 ease-in-out md:static md:w-60 md:translate-x-0"
    :class="open ? 'translate-x-0' : '-translate-x-full'"
  >
    <div class="flex pt-6 md:pt-8 pb-2 items-center px-5 gap-2 justify-between">
      <span class="font-lightbase text-3xl text-forest font-black">Lightbase</span>
      <button
        class="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 md:hidden"
        aria-label="Close navigation"
        @click="emit('close')"
      >
        <XMarkIcon class="h-5 w-5" />
      </button>
    </div>

    <nav class="flex-1 space-y-1 overflow-y-auto px-3 py-2">
      <SidebarSection
        v-for="section in visibleSections"
        :id="section.id"
        :key="section.id"
        :label="section.label"
        :expanded="isExpanded(section.id)"
        :reorderable="reorderable"
        :has-active-child="sectionHasActive(section)"
        :drop-place="dropHint?.id === section.id ? dropHint.place : null"
        :dragging="draggingId === section.id"
        @toggle="toggle(section.id)"
        @dragstart="onSectionDragStart(section.id)"
        @dragover="onSectionDragOver(section.id, $event)"
        @drop="onSectionDrop(section.id, $event)"
        @dragend="onSectionDragEnd"
      >
        <SidebarNavItem
          v-for="item in section.items"
          :key="item.to"
          :to="item.to"
          :icon="item.icon"
          :label="item.label"
          :active="item.isActive(route.path)"
        />
      </SidebarSection>
    </nav>

    <div class="border-t border-gray-200 px-3 py-3">
      <button
        v-if="authStore.canAccessStaff"
        type="button"
        class="mb-2 flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-left text-xs text-gray-500 hover:bg-gray-50 hover:text-gray-700"
        @click="customersStore.openSpotlight()"
      >
        <span class="inline-flex items-center gap-2">
          <MagnifyingGlassIcon class="h-4 w-4" />
          Search customers
        </span>
        <kbd
          class="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[10px] text-gray-400"
        >/</kbd>
      </button>
      <div class="flex items-center justify-between px-1">
        <span class="truncate text-xs text-gray-500">
          {{ localDev ? 'Local dev' : authStore.userEmail }}
        </span>
        <button
          v-if="!localDev"
          class="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          @click="authStore.signOut()"
        >
          <ArrowRightStartOnRectangleIcon class="h-4 w-4" />
        </button>
      </div>
    </div>
  </aside>
</template>
